import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'react-icons-kit';
import { ic_delete_outline } from 'react-icons-kit/md/ic_delete_outline';
import { HoverTooltip } from 'components/common/HoverTooltip';
import { ic_add_circle_outline } from 'react-icons-kit/md/ic_add_circle_outline';
import { ic_keyboard_arrow_down } from 'react-icons-kit/md/ic_keyboard_arrow_down';
import { ic_keyboard_arrow_right } from 'react-icons-kit/md/ic_keyboard_arrow_right';
import {
  inputClasses,
  buttonClasses,
} from 'components/common/tailwindClassnames';
import {
  SchemaField,
  FIELD_TYPES,
  STRING_FORMATS,
} from './schema-builder/types';
import {
  createEmptyField,
  fieldsToJsonSchema,
  parseJsonSchemaToFields,
} from './schema-builder/helpers';
import { TypeSelect } from './schema-builder/TypeSelect';
import { SelectMenu } from './schema-builder/SelectMenu';

// ── FieldCard ──

type FieldCardProps = {
  field: SchemaField;
  index: number;
  depth: number;
  onUpdate: (index: number, field: SchemaField) => void;
  onRemove: (index: number) => void;
  readOnly: boolean;
};

function FieldCard({
  field,
  index,
  depth,
  onUpdate,
  onRemove,
  readOnly,
}: FieldCardProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const handleChange = useCallback(
    (key: keyof SchemaField, value: any) => {
      const updated = { ...field, [key]: value };
      if (key === 'type') {
        updated.format = '';
        updated.enumValues = '';
        updated.minimum = '';
        updated.maximum = '';
        if (value === 'object' && updated.children.length === 0) {
          updated.children = [];
        }
        if (value === 'array') {
          updated.arrayItemType = 'string';
          updated.children = [];
        }
      }
      if (
        key === 'arrayItemType' &&
        value === 'object' &&
        updated.children.length === 0
      ) {
        updated.children = [];
      }
      onUpdate(index, updated);
    },
    [field, index, onUpdate],
  );

  const handleChildUpdate = useCallback(
    (childIndex: number, child: SchemaField) => {
      const newChildren = [...field.children];
      newChildren[childIndex] = child;
      onUpdate(index, { ...field, children: newChildren });
    },
    [field, index, onUpdate],
  );

  const handleChildRemove = useCallback(
    (childIndex: number) => {
      onUpdate(index, {
        ...field,
        children: field.children.filter((_, i) => i !== childIndex),
      });
    },
    [field, index, onUpdate],
  );

  const handleAddChild = useCallback(() => {
    onUpdate(index, {
      ...field,
      children: [...field.children, createEmptyField()],
    });
  }, [field, index, onUpdate]);

  const hasOptions = field.type !== 'object'; // all types except object have expandable options

  return (
    <div
      className={`mb-4 overflow-hidden rounded-lg border border-ui-divider-lines bg-white`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Expand toggle */}
        {hasOptions && (
          <button
            onClick={() => setExpanded((e) => !e)}
            className="p-0.5 text-ui-text-secondary-text transition-colors hover:text-ui-text-primary-text"
            type="button"
            data-testid="field-expand-btn"
            aria-expanded={expanded}
          >
            <Icon
              icon={expanded ? ic_keyboard_arrow_down : ic_keyboard_arrow_right}
              size={18}
            />
          </button>
        )}

        {/* Field name */}
        <input
          value={field.name}
          onChange={(e) => handleChange('name', e.target.value)}
          placeholder={t('e.g. device_id')}
          disabled={readOnly}
          className={`${inputClasses.standard} w-44 font-mono text-sm`}
          data-testid="field-name-input"
        />

        {/* Type selector */}
        <TypeSelect
          value={field.type}
          options={FIELD_TYPES}
          onChange={(v) => handleChange('type', v)}
          disabled={readOnly}
          className="w-44"
          dataTestId="field-type-select"
        />

        {/* Required */}
        <label className="ml-1 flex cursor-pointer select-none items-center gap-1.5 text-sm text-ui-text-primary-text">
          <input
            type="checkbox"
            checked={field.required}
            onChange={(e) => handleChange('required', e.target.checked)}
            // Enter toggles too (native checkboxes only respond to Space), to match
            // the Enter-to-activate convention used elsewhere in the builder.
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleChange('required', !field.required);
              }
            }}
            disabled={readOnly}
            className="accent-ui-cta-primary"
            data-testid="field-required-checkbox"
          />
          {t('Required')}
        </label>

        <div className="flex-1" />

        {/* Remove — trash icon */}
        {!readOnly && (
          // Same tooltip as the transformation builder's trash; portaled so
          // the panel's edges can't clip it.
          <HoverTooltip
            content={t('Remove field')}
            position="top"
            focusable={false}
            portal
          >
            <button
              onClick={() => onRemove(index)}
              className="flex items-center p-0.5 text-greyscale-5 transition-colors hover:text-ui-status-error-destructive"
              type="button"
              aria-label={t('Remove field')}
              data-testid="field-remove-btn"
            >
              <Icon icon={ic_delete_outline} size={20} />
            </button>
          </HoverTooltip>
        )}
      </div>

      {/* Expanded options */}
      {expanded && hasOptions && (
        <div className="flex flex-col gap-3 border-t border-ui-field-outline bg-unlisted-very-light-gray px-4 pb-4 pt-1">
          {/* Description — available for all types */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-ui-text-primary-text">
              {t('Description')}
            </label>
            <input
              value={field.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder={t('Optional description')}
              disabled={readOnly}
              className={`${inputClasses.standard} text-sm`}
              data-testid="field-description-input"
            />
          </div>

          {/* String-specific: format, enum */}
          {field.type === 'string' && (
            <>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-ui-text-primary-text">
                  {t('Format')}
                </label>
                <SelectMenu
                  value={field.format}
                  options={STRING_FORMATS.map((f) => ({
                    value: f.value,
                    label: t(f.labelKey as any),
                  }))}
                  onChange={(v) => handleChange('format', v)}
                  disabled={readOnly}
                  className="w-full"
                  dataTestId="field-format-select"
                  ariaLabel={t('Format')}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-ui-text-primary-text">
                  {t('Allowed values (comma-separated)')}
                </label>
                <input
                  value={field.enumValues}
                  onChange={(e) => handleChange('enumValues', e.target.value)}
                  placeholder={t('e.g. active, inactive, pending')}
                  disabled={readOnly}
                  className={`${inputClasses.standard} font-mono text-sm`}
                  data-testid="field-enum-input"
                />
              </div>
            </>
          )}

          {/* Number/Integer: min, max */}
          {(field.type === 'number' || field.type === 'integer') && (
            <div className="flex gap-3">
              <div className="flex flex-1 flex-col gap-1">
                <label className="text-xs font-medium text-ui-text-primary-text">
                  {t('Minimum')}
                </label>
                <input
                  value={field.minimum}
                  onChange={(e) => handleChange('minimum', e.target.value)}
                  placeholder={t('No minimum')}
                  disabled={readOnly}
                  className={`${inputClasses.standard} font-mono text-sm`}
                  data-testid="field-minimum-input"
                />
              </div>
              <div className="flex flex-1 flex-col gap-1">
                <label className="text-xs font-medium text-ui-text-primary-text">
                  {t('Maximum')}
                </label>
                <input
                  value={field.maximum}
                  onChange={(e) => handleChange('maximum', e.target.value)}
                  placeholder={t('No maximum')}
                  disabled={readOnly}
                  className={`${inputClasses.standard} font-mono text-sm`}
                  data-testid="field-maximum-input"
                />
              </div>
            </div>
          )}

          {/* Array: item type */}
          {field.type === 'array' && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-ui-text-primary-text">
                {t('Item type')}
              </label>
              <TypeSelect
                value={field.arrayItemType}
                options={FIELD_TYPES.filter((ft) => ft.value !== 'array')}
                onChange={(v) => handleChange('arrayItemType', v)}
                disabled={readOnly}
                className="w-44"
                dataTestId="field-array-item-type-select"
              />
            </div>
          )}
        </div>
      )}

      {/* Nested children for object type or array with object items */}
      {(field.type === 'object' ||
        (field.type === 'array' && field.arrayItemType === 'object')) && (
        <div className="mb-0.5 ml-8 mr-0.5 border-l-2 border-ui-cta-primary pl-4">
          <div className="mb-3 mt-2 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-ui-text-primary-text">
              {field.type === 'array' ? t('Item fields') : t('Nested fields')}
            </span>
            {!readOnly && (
              <button
                onClick={handleAddChild}
                className={`${buttonClasses.outlineXs} mr-2`}
                type="button"
              >
                {t('Add Field')}
              </button>
            )}
          </div>
          {field.children.length === 0 && (
            <p className="py-2 text-sm text-ui-text-primary-text">
              {t('No nested fields')}
            </p>
          )}
          {field.children.map((child, i) => (
            <FieldCard
              key={child.id}
              field={child}
              index={i}
              depth={depth + 1}
              onUpdate={handleChildUpdate}
              onRemove={handleChildRemove}
              readOnly={readOnly}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Empty State ──

function EmptyState({
  onAdd,
  readOnly,
}: {
  onAdd: () => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="py-12 text-center">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-teal-t10">
        <Icon
          icon={ic_add_circle_outline}
          size={32}
          className="text-ui-cta-primary"
        />
      </div>
      <p className="mb-1 text-base font-medium text-ui-text-primary-text">
        {t('No fields defined yet')}
      </p>
      <p className="mb-4 text-sm text-ui-text-primary-text">
        {t('Add fields to define the structure of your webhook data')}
      </p>
      {!readOnly && (
        <button
          onClick={onAdd}
          className={buttonClasses.primarySm}
          type="button"
          data-testid="empty-state-add-btn"
        >
          {t('Add Your First Field')}
        </button>
      )}
    </div>
  );
}

// ── Main component ──

export type JsonSchemaBuilderProps = {
  onGenerate: (schema: object) => void;
  readOnly?: boolean;
  initialSchema?: object | null;
  hideHeader?: boolean;
  /** When set, the builder syncs its fields from this schema (for bidirectional editing). */
  externalSchema?: object | null;
};

const JsonSchemaBuilder = ({
  onGenerate,
  readOnly = false,
  initialSchema,
  hideHeader = false,
  externalSchema,
}: JsonSchemaBuilderProps) => {
  const { t } = useTranslation();

  const [fields, setFields] = useState<SchemaField[]>(() => {
    const parsed = parseJsonSchemaToFields(initialSchema);
    return parsed ?? [];
  });

  const hasInteracted = React.useRef(false);
  const onGenerateRef = React.useRef(onGenerate);
  onGenerateRef.current = onGenerate;
  // Track the last schema JSON the builder itself generated, to avoid re-parsing it
  const lastInternalSchemaRef = React.useRef<string>('');

  const schema = useMemo(() => fieldsToJsonSchema(fields), [fields]);

  useEffect(() => {
    if (hasInteracted.current) {
      lastInternalSchemaRef.current = JSON.stringify(schema);
      onGenerateRef.current(schema);
    }
  }, [schema]);

  // Sync from external schema (e.g. user edited the Monaco editor or pasted a sample)
  useEffect(() => {
    if (externalSchema === undefined || externalSchema === null) return;
    // Skip if this is the same schema the builder just generated
    const externalJson = JSON.stringify(externalSchema);
    if (externalJson === lastInternalSchemaRef.current) return;
    const parsed = parseJsonSchemaToFields(externalSchema);
    if (parsed) {
      setFields(parsed);
    }
  }, [externalSchema]);

  const handleAdd = useCallback(() => {
    hasInteracted.current = true;
    setFields((prev) => [...prev, createEmptyField()]);
  }, []);

  const handleUpdate = useCallback((index: number, field: SchemaField) => {
    hasInteracted.current = true;
    setFields((prev) => prev.map((f, i) => (i === index ? field : f)));
  }, []);

  const handleRemove = useCallback((index: number) => {
    hasInteracted.current = true;
    setFields((prev) => prev.filter((_, i) => i !== index));
  }, []);

  return (
    <div
      className={`flex flex-col ${hideHeader ? '' : 'overflow-hidden rounded-lg border border-ui-field-outline bg-white'}`}
      data-testid="JsonSchemaBuilder"
    >
      {/* Header — hidden when embedded inside a collapsible */}
      {!hideHeader && (
        <div className="flex items-center justify-between border-b border-ui-field-outline bg-unlisted-very-light-gray px-4 py-3 text-sm font-semibold text-ui-text-primary-text">
          <span>{t('Schema Builder')}</span>
          {!readOnly && (
            <button
              onClick={handleAdd}
              className={`${buttonClasses.outlineXs} mr-2`}
              type="button"
              data-testid="add-field-btn"
            >
              {t('Add Field')}
            </button>
          )}
        </div>
      )}

      {/* Inline toolbar when header is hidden */}
      {hideHeader && fields.length > 0 && !readOnly && (
        <div className="mb-3 flex justify-end">
          <button
            onClick={handleAdd}
            className={`${buttonClasses.outlineXs} mr-2`}
            type="button"
            data-testid="add-field-btn"
          >
            {t('Add Field')}
          </button>
        </div>
      )}

      {/* Fields */}
      <div className={hideHeader ? '' : 'p-4'}>
        {fields.length === 0 ? (
          <EmptyState onAdd={handleAdd} readOnly={readOnly} />
        ) : (
          fields.map((field, i) => (
            <FieldCard
              key={field.id}
              field={field}
              index={i}
              depth={0}
              onUpdate={handleUpdate}
              onRemove={handleRemove}
              readOnly={readOnly}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default JsonSchemaBuilder;
